import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';

import { API_URL } from '../../constants/urls';

interface InvitationInfo {
  organization_name: string;
  role: string;
  inviter_name: string | null;
  user_email: string;
  is_existing_user: boolean;
}

export default function AcceptInvitationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitationInfo, setInvitationInfo] = useState<InvitationInfo | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Verify the invitation token
    fetch(`${API_URL}/invitation/verify?token=${encodeURIComponent(token)}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error('Invalid or expired invitation');
        }
        return response.json();
      })
      .then((data) => {
        setInvitationInfo(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to verify invitation');
        setLoading(false);
      });
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // For new users, validate password confirmation
    if (!invitationInfo?.is_existing_user && password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    // For new users, validate password length
    if (!invitationInfo?.is_existing_user && password.length < 8) {
      setFormError('Password must be at least 8 characters');
      return;
    }

    // For new users, validate name fields
    if (!invitationInfo?.is_existing_user && (!firstName || !lastName)) {
      setFormError('Please provide your first and last name');
      return;
    }

    setFormError(null);

    setSubmitting(true);

    try {
      const requestBody: any = {
        token,
        password,
      };

      // Only include name fields for new users
      if (!invitationInfo?.is_existing_user) {
        requestBody.first_name = firstName;
        requestBody.last_name = lastName;
      }

      const response = await fetch(`${API_URL}/invitation/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to accept invitation');
      }

      // Always redirect to login to ensure proper authentication
      if (invitationInfo?.is_existing_user) {
        navigate('/login', {
          state: {
            message:
              'Invitation accepted! Please login to access the organization.',
          },
        });
      } else {
        navigate('/login', {
          state: { message: 'Account created successfully! Please login.' },
        });
      }
    } catch (err: any) {
      setFormError(err.message || 'Failed to accept invitation');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !invitationInfo) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ mt: 8 }}>
          <Alert severity="error">{error || 'Invalid invitation'}</Alert>
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Accept Invitation
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            {invitationInfo.inviter_name
              ? `${invitationInfo.inviter_name} has invited you`
              : 'You have been invited'}{' '}
            to join <strong>{invitationInfo.organization_name}</strong> as a{' '}
            <strong>{invitationInfo.role}</strong>.
            {invitationInfo.is_existing_user && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2">
                  You already have an account. Enter your password to accept
                  this invitation.
                </Typography>
              </Box>
            )}
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Your email: <strong>{invitationInfo.user_email}</strong>
          </Typography>

          <form onSubmit={handleSubmit}>
            {formError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {formError}
              </Alert>
            )}

            {/* Show name fields only for new users */}
            {!invitationInfo.is_existing_user && (
              <>
                <TextField
                  fullWidth
                  label="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  margin="normal"
                  required
                  autoFocus
                />

                <TextField
                  fullWidth
                  label="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  margin="normal"
                  required
                />
              </>
            )}

            <TextField
              fullWidth
              label={
                invitationInfo.is_existing_user
                  ? 'Enter your password'
                  : 'Password'
              }
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              margin="normal"
              required
              helperText={
                invitationInfo.is_existing_user
                  ? 'Enter your existing password to accept the invitation'
                  : 'Must be at least 8 characters'
              }
              autoFocus={invitationInfo.is_existing_user}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Show confirm password only for new users */}
            {!invitationInfo.is_existing_user && (
              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={submitting}
            >
              {submitting ? (
                <CircularProgress size={24} />
              ) : invitationInfo.is_existing_user ? (
                'Accept Invitation'
              ) : (
                'Create Account'
              )}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
}
